import dayjs from 'dayjs';

import {
  useEffect,
  useState
} from "react";

import {
  useNavigate,
  useLoaderData
} from "react-router-dom";

import ConfirmationModal from "../_Shared/ConfirmationModal.js";

import RoenBranchSelector from "../_Shared/RoenBranchSelector.js";

import {
  fetchClient
} from '../../util/fetchClient.js';

import {
  getStartOfQuarter,
  getStartOfHalfYear,
} from "../../util/date.js";

import { 
  useAuth
} from '../../hooks/useAuth.js';

import {
  DEFAULT_EXPENSE_TAXONOMY_URL,
  DEFAULT_INCOME_TAXONOMY_URL
} from '../../categories/index.js';

import config from '../../config.js';

import Datepicker from "react-tailwindcss-datepicker";

const pageLimit = 10;

const DEFAULT_BRANCH = { id: 'all', name: 'Todas' };

const fetchBranches = async () => {
  const { body: branches } = await fetchClient('/v1/branches');

  return [
    DEFAULT_BRANCH,
    ...branches.data,
  ];
};

const formatDate = date => dayjs(date).format('DD/MM/YYYY');

const fetchLedgerEntries = async ({ offset=0, limit=pageLimit, startTime, endTime, branchId = 'all' }) => {
  const startOfWeek = dayjs().startOf('week').toDate();
  const eod = dayjs().endOf('day').toDate();
  const start = (startTime || startOfWeek).toISOString();
  const end = (endTime || eod).toISOString();
  const { body: ledgerEntries } = await fetchClient(`/v1/ledger?offset=${offset}&limit=${limit}&start_time=${start}&end_time=${end}&branch_id=${branchId}`);
  return { ledgerEntries: ledgerEntries.data };
};

const fetchCategories = async (categoriesURL) => {
  const { body: categories } = await fetchClient('', { credentials: 'same-origin' }, categoriesURL);
  return { categories };
};

export const loader = () => fetchLedgerEntries({ offset: 0, limit: pageLimit });

const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ')
};

export default function Ledger() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const newButtonClick = () => navigate('/ledger/new');
  const editButtonClick = (id) => navigate(`/ledger/edit/${id}`);
  const deleteButtonClick = async (ledgerEntry) => {
    setSelectedLedgerEntry(ledgerEntry);
    setShowDeleteModal(true);
  };
  const deleteHandler = async () => {
    await fetchClient(`/v1/ledger/${selectedLedgerEntry.id}`, { method: 'DELETE' });
    setShowDeleteModal(false);
    window.location.reload();
  };
  const cancelHandler = () => {
    setShowDeleteModal(false);
  };
  const loaderData = useLoaderData();

  const [incomeCategories, setIncomeCategories] = useState({});
  const [expenseCategories, setExpenseCategories] = useState({});
  const [selectedLedgerEntry, setSelectedLedgerEntry] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lastPage, setLastPage] = useState(Infinity);
  const [currentPage, setCurrentPage] = useState(1);
  const [ledgerEntries, setLedgerEntries] = useState(loaderData?.ledgerEntries || []);

  const eod = dayjs().endOf('day');
  const startOfWeek = dayjs().startOf('week');
  const startOfMonth = dayjs().startOf('month');
  const startOfQuarter = dayjs(getStartOfQuarter(new Date()));
  const startOfHalfYear = dayjs(getStartOfHalfYear(new Date()));
  const startOfYear = dayjs().startOf('year');
  const [datesSelected, setDatesSelected] = useState({ startDate: startOfWeek, endDate: eod });
  const [branches, setBranches] = useState([]);
  const [branchSelected, setBranchSelected] = useState(DEFAULT_BRANCH);

  const handleDateChange = (newDates) => {
    setDatesSelected({
      startDate: dayjs(newDates.startDate).startOf('day'),
      endDate: dayjs(newDates.endDate).endOf('day'),
    });
  };

  useEffect(() => {
    async function getBranches() {
      if (!ignore) {
        const branches = await fetchBranches();
        setBranches(branches);
      }
    }

    let ignore = false;
    getBranches();
    return () => {
      ignore = true;
    }
  }, []);

  useEffect(() => {
    async function getCategories() {
      if (!ignore) {
        const { categories: newIncomeCategories } = await fetchCategories(user.income_taxonomy_url || DEFAULT_INCOME_TAXONOMY_URL);
        setIncomeCategories(newIncomeCategories);

        const { categories: newExpenseCategories } = await fetchCategories(user.expense_taxonomy_url || DEFAULT_EXPENSE_TAXONOMY_URL);
        setExpenseCategories(newExpenseCategories);
      }
    }

    let ignore = false;
    getCategories();
    return () => {
      ignore = true;
    }
  }, [user.income_taxonomy_url, user.expense_taxonomy_url]);

  useEffect(() => {
    async function getLedgerEntries() {
      if (!ignore) {
        const { ledgerEntries: updatedLedgerEntries } = await fetchLedgerEntries({ offset: 0, limit: pageLimit, startTime: datesSelected.startDate && new Date(datesSelected.startDate), endTime: datesSelected.endDate && new Date(datesSelected.endDate), branchId: branchSelected.id });
        setLedgerEntries(updatedLedgerEntries);
        setLastPage(Infinity);
        setCurrentPage(1);
      }
    };

    let ignore = false;
    getLedgerEntries();
    return () => {
      ignore = true;
    }
  }, [datesSelected, branchSelected]);

  const isLastPage = () => lastPage <= currentPage || ledgerEntries.length < pageLimit || ledgerEntries.length === 0;

  const handlePreviousPage = async () => {
    if (currentPage === 1) {
      return;
    }

    const newPage = currentPage - 1;
    const pageOffset = newPage * pageLimit - pageLimit;
    const { ledgerEntries: updatedLedgerEntries } = await fetchLedgerEntries({ offset: pageOffset, limit: pageLimit, startTime: datesSelected.startDate && new Date(datesSelected.startDate), endTime: datesSelected.endDate && new Date(datesSelected.endDate), branchId: branchSelected.id });
    setCurrentPage(currentPage - 1);
    setLedgerEntries(updatedLedgerEntries);
    setLastPage(Infinity);
  };

  const handleNextPage = async () => {
    if (isLastPage()) {
      return;
    }

    const newPage = currentPage + 1;
    const pageOffset = newPage * pageLimit - pageLimit;
    const { ledgerEntries: updatedLedgerEntries } = await fetchLedgerEntries({ offset: pageOffset, limit: pageLimit, startTime: datesSelected.startDate && new Date(datesSelected.startDate), endTime: datesSelected.endDate && new Date(datesSelected.endDate), branchId: branchSelected.id });
    if (updatedLedgerEntries.length) {
      setCurrentPage(newPage);
      setLedgerEntries(updatedLedgerEntries);
      setLastPage(Infinity);
    } else {
      setLastPage(currentPage);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {showDeleteModal && ( <ConfirmationModal deleteHandler={deleteHandler} cancelHandler={cancelHandler}/> )}

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto">
            <h1 className="mt-3 text-base font-semibold leading-6 text-gray-900 -mr-2">Sucursal</h1>
            <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto -ml-3">
              <RoenBranchSelector
                branchOptions={branches}
                branchSelected={branchSelected}
                onChange={setBranchSelected}
              />
            </div>
            <h1 className="mt-3 text-base font-semibold leading-6 text-gray-900 -ml-2 -pl-5 -mr-5">Periodo</h1>
            <div className='py-1.5'>
              <Datepicker
                  i18n={"es-mx"}
                  inputClassName={"relative transition-all duration-300 py-1.5 pl-4 pr-10 w-full min-w-72 max-w-72 border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-lg tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-indigo-500 focus:ring-indigo-500/20"}
                  separator={"-"}
                  primaryColor={"indigo"}
                  useRange={true}
                  displayFormat={"DD/MM/YYYY"}
                  placeholder={`${formatDate(startOfWeek)} - ${formatDate(eod)}`}
                  value={datesSelected}
                  showShortcuts={true}
                  onChange={handleDateChange}
                  configs={{
                    shortcuts: {
                      thisWeek: {
                        text: "Esta semana",
                        period: {
                          start: startOfWeek,
                          end: eod
                        },
                      },
                      thisMonth: {
                        text: "Este mes",
                        period: {
                          start: startOfMonth,
                          end: eod
                        },
                      },
                      thisQuarter: {
                        text: "Este trimestre",
                        period: {
                          start: startOfQuarter,
                          end: eod
                        },
                      },
                      thisHalfYear: {
                        text: "Este semestre",
                        period: {
                          start: startOfHalfYear,
                          end: eod
                        },
                      },
                      thisYear: {
                        text: "Este año",
                        period: {
                          start: startOfYear,
                          end: eod
                        },
                      },
                    }
                  }}
              />
            </div>
          </div>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <a
            type="button"
            className="mr-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            href={`${config.roenBackendURL}/v1/ledger/csv?start_time=${datesSelected.startDate}&end_time=${datesSelected.endDate}&branch_id=${branchSelected.id}`}
          >
            Descargar CSV
          </a>
          <button
            type="button"
            className="rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={newButtonClick} >
            Nuevo
          </button>
        </div>
      </div>

      <div className="mt-5 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="top-0 z-10 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Descripción
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Tipo
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Monto
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Categoría
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Folio
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Registro
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Sucursal
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {ledgerEntries.map((ledgerEntry, _ledgerEntryIdx) => (
                    <tr key={ledgerEntry.id}>
                      <td
                        className={classNames(
                          'whitespace-nowrap py-3 pl-2 pr-3 text-sm font-medium text-gray-900 sm:pl-6'
                        )}
                      >
                        {ledgerEntry.description || 'N/A'}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        <div className="flex">
                          <div
                            className={classNames(
                              (ledgerEntry.type === 'income' ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-red-700 bg-red-50 ring-red-600/10'),
                              'rounded-md py-1 px-2 text-sm ring-1 ring-inset'
                            )}
                          >
                            {ledgerEntry.type === 'income' ? 'Ingreso' : 'Egreso'}
                          </div>
                        </div>
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        <div className="flex gap-x-6">
                          <div className="flex-auto">
                            <div className="flex items-start gap-x-3">
                              <div className="text-sm leading-6 text-gray-900">{new Intl.NumberFormat('es-MX', { style: 'currency', currency: ledgerEntry.currency }).format(ledgerEntry.amount)}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {incomeCategories[ledgerEntry.category_id]?.name || expenseCategories[ledgerEntry.category_id]?.name || 'Desconocida'}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        { ledgerEntry.invoice_id || 'N/A' }
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {dayjs(ledgerEntry.created_at).format('DD/MM/YYYY')}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {ledgerEntry.branch_name}
                      </td>
                      <td
                        className={classNames(
                          'relative whitespace-nowrap py-3 pr-4 text-right font-medium sm:pr-6'
                        )}
                      >
                        <button type="button" className="text-indigo-600 hover:text-indigo-900" onClick={() => editButtonClick(ledgerEntry.id)}>Editar</button>
                        <button type="button" className="text-rose-600 hover:text-rose-900 pl-5" onClick={() => deleteButtonClick(ledgerEntry)}>Borrar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <nav
        className="flex items-center justify-between bg-white px-4 py-4 sm:px-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Página <span className="font-medium">{currentPage}</span>, mostrando <span className="font-medium">{ledgerEntries.length}</span> resultados
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            disabled={currentPage === 1}
            onClick={handlePreviousPage}
            className='disabled:bg-gray-300 disabled:opacity-50 relative inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0'
          >
            Anterior
          </button>
          <button
            disabled={isLastPage()}
            onClick={handleNextPage}
            className='disabled:bg-gray-300 disabled:opacity-50 relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0'
          >
            Siguiente
          </button>
        </div>
      </nav>
    </div>
  )
}
